//@flow
import React, {useState, useEffect} from 'react';
import {Autocomplete} from '@material-ui/lab';
import SwitchableSection from '../../../../../elements/SwitchableSection';
import {
  ButtonBase,
  Chip,
  makeStyles,
  Typography,
  FormControlLabel,
  Box,
} from '@material-ui/core';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';

import categoryMenu1 from '../../../../../assets/img/home-pages/category-menu-1.webp';
import categoryMenu2 from '../../../../../assets/img/home-pages/category-menu-2.webp';
import categoryMenu3 from '../../../../../assets/img/home-pages/category-menu-3.webp';
import {HOME_PAGE_SECTION_NAMES} from '../../../utils/home-page-sections/constants';
import {useCategories} from '../../../../categories/utils';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';

import {
  useProductCategoryLayoutActions,
  useProductCategoryLayout,
} from '../../../utils/product-category-layout';
import type {Category, CategoryMenuLayoutType} from '../../../../../entities';
import {useAlert} from '../../../../../elements/Alert/AlertContext';
import {useIsFeatureInTier} from '../../../../../utils/component';
import {useTierRestrictionActions} from '../../../../TierRestriction/utils';
import PremiumFeatureBadge from '../../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import {useStoreBusinessModel} from '../../../../store/utils';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';
import {getImageUrl} from '../../../../../utils/image_provider';
import {DragIndicator} from '@material-ui/icons';
import {arrayMove, moveBetweenArrays} from '../../../../../utils/array';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
    width: '100%',
  },
  items: {
    display: 'flex',
    gap: '.5rem',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  itemButton: {
    flex: '1 1 40%',
    padding: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    position: 'relative',
    height: '200px',

    [theme.breakpoints.up('lg')]: {
      flex: '1 1 25%',
    },
  },
  subtitle: {
    fontSize: '.875rem',
    fontWeight: 500,
    color: colors.grey900,
    display: 'block',
    textAlign: 'left',
  },
  item: {
    width: '100%',
    height: '100%',
    background: '#F2F2F2',
    borderRadius: '.5rem',
    padding: '1.5rem 1rem 1rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '.75rem',
    cursor: 'pointer',
    position: 'relative',
  },
  itemLabel: {
    display: 'block',
    textAlign: 'center',
    color: colors.grey600,
    fontSize: '.75rem',
    fontWeight: 600,
  },
  layoutThumbnail: {
    width: '100%',
    maxWidth: '200px',
  },
  checkbox: {
    position: 'absolute',
    top: '.5rem',
    left: '.5rem',
    width: '1.25rem',
    height: '1.25rem',
    background: '#fff',
    borderRadius: '50%',
    border: '1px solid #E0E0E0',
    transition: 'border .2s ease-in-out',

    '&[data-checked=true]': {
      border: `.35rem solid ${colors.primary}`,
    },
  },
  disabledRoot: {
    background: colors.grey300,
  },
  groupingSection: {
    marginTop: '.5rem',
    borderRadius: '8px',
    backgroundColor: colors.grey100,
  },
  groupContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginTop: '.5rem',
  },
  groupBox: {
    minHeight: '80px',
    padding: '0.75rem',
    border: `2px dashed ${colors.grey400}`,
    borderRadius: '8px',
    backgroundColor: colors.grey100,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  groupTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.grey700,
    marginBottom: '0.5rem',
  },
  draggingOver: {
    backgroundColor: colors.lightPurple,
    borderColor: colors.primary,
  },
  saveButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '.5rem',
    paddingTop: '.5rem',
    borderTop: `1px solid ${colors.grey300}`,
  },
  categoryDragItem: {
    padding: '0.5rem',
    cursor: 'move',
    border: '1px solid #E0E0E0',
    borderRadius: '8px',
    backgroundColor: colors.grey200,
    display: 'flex',
    gap: '1rem',
    width: '100%',
    fontSize: '0.875rem',
    alignItems: 'center',
    justifyContent: 'flex-start',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',

    '& span': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
    },
  },
  categoryImage: {
    width: '32px',
    height: '32px',
    objectFit: 'cover',
    borderRadius: '8px',
  },
  dragIcon: {
    color: colors.grey500,
    fontSize: '1.25rem',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
}));

type CategoryLayout = {
  name: CategoryMenuLayoutType,
  label: string,
  image: string,
  businessModel: string[],
  maxGroups?: number,
};

const LAYOUTS: CategoryLayout[] = [
  {
    name: 'DEFAULT',
    label: 'Diseño 1',
    image: categoryMenu1,
    businessModel: ['PRODUCT', 'SERVICE'],
  },
  {
    name: '2_COLUMNS',
    label: 'Diseño 2',
    image: categoryMenu2,
    businessModel: ['PRODUCT'],
    maxGroups: 2,
  },
  {
    name: '3_COLUMNS',
    label: 'Diseño 3',
    image: categoryMenu3,
    businessModel: ['PRODUCT'],
    maxGroups: 3,
  },
];

const DEFAULT_LAYOUT: CategoryMenuLayoutType = LAYOUTS[0].name;
const CATEGORY_SELECTED_LIMIT = 6;

type Props = {
  sectionEnabled?: boolean,
};

export default function CategoryMenu(props: Props) {
  const {sectionEnabled} = props;

  const classes = useStyles();
  const {showAlert} = useAlert();
  const {showTierRestrictionModal} = useTierRestrictionActions();

  const businessModel = useStoreBusinessModel();

  const {updateCategoryMenuLayout, loading} = useProductCategoryLayoutActions();
  const productCategoryLayout = useProductCategoryLayout();
  const hasCategoryLayoutFeature = useIsFeatureInTier(
    'PRODUCT_CATEGORY_LAYOUT'
  );

  const categories = useCategories();
  const [layoutSelected, setLayoutSelected] = useState<CategoryMenuLayoutType>(
    DEFAULT_LAYOUT
  );
  const [categorySelected, setCategorySelected] = useState<Category[]>([]);
  const [groupingEnabled, setGroupingEnabled] = useState<boolean>(false);
  const [categoryGroups, setCategoryGroups] = useState<string[][]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  useEffect(() => {
    if (!productCategoryLayout?.productCategoryMenu || !categories) return;
    if (!hasCategoryLayoutFeature) return;
    const {productCategoryMenu} = productCategoryLayout;
    const categoriesSelected = productCategoryMenu.categories
      ?.map(id => {
        return categories.find(category => category.id === id);
      })
      .filter(Boolean);
    setLayoutSelected(productCategoryMenu.layout);
    if (!categoriesSelected) return;
    setCategorySelected(categoriesSelected);

    if (productCategoryMenu.groups && productCategoryMenu.groups.length > 0) {
      setCategoryGroups(productCategoryMenu.groups);
      setGroupingEnabled(true);
    } else {
      const currentLayout = LAYOUTS.find(
        layout => layout.name === productCategoryMenu.layout
      );
      if (currentLayout?.maxGroups) {
        const defaultGroups = Array.from(
          {length: currentLayout.maxGroups},
          (_, index) =>
            index === 0 ? productCategoryMenu.categories || [] : []
        );
        setCategoryGroups(defaultGroups);
      }
    }
  }, [categories, hasCategoryLayoutFeature, productCategoryLayout]);

  const limitedCategories = layoutSelected !== DEFAULT_LAYOUT;

  const handleSaveCategoryMenu = () => {
    const categoriesId = categorySelected.map(category => category.id);
    const isDefaultLayout = layoutSelected === DEFAULT_LAYOUT;

    if (!isDefaultLayout && categorySelected.length === 0) return;

    const menuData = {
      layout: layoutSelected,
      categories: categoriesId,
      enabled: true,
      groups: groupingEnabled && !isDefaultLayout ? categoryGroups : undefined,
    };

    updateCategoryMenuLayout(menuData).then(() => {
      showAlert('Se ha guardado el menú de categorías', {
        duration: 5000,
        type: 'success',
      });
      setHasUnsavedChanges(false);
    });
  };

  const handleSelectLayout = (name: CategoryMenuLayoutType) => {
    if (!hasCategoryLayoutFeature) {
      showTierRestrictionModal();
      return;
    }

    const previousLayout = LAYOUTS.find(
      layout => layout.name === layoutSelected
    );
    const newLayout = LAYOUTS.find(layout => layout.name === name);

    setLayoutSelected(name);
    setHasUnsavedChanges(true);

    if (name === DEFAULT_LAYOUT) {
      setGroupingEnabled(false);
      setCategoryGroups([]);
    } else if (newLayout?.maxGroups) {
      if (previousLayout?.maxGroups !== newLayout.maxGroups) {
        const newGroups = Array.from(
          {length: newLayout.maxGroups},
          (_, index) => {
            if (index === 0) {
              return categorySelected.map(cat => cat.id);
            }
            return [];
          }
        );
        setCategoryGroups(newGroups);
      } else if (categoryGroups.length === 0) {
        const initialGroups = Array.from(
          {length: newLayout.maxGroups},
          (_, index) => (index === 0 ? categorySelected.map(cat => cat.id) : [])
        );
        setCategoryGroups(initialGroups);
      }
    }
  };

  const handleGroupingToggle = (enabled: boolean) => {
    setGroupingEnabled(enabled);
    setHasUnsavedChanges(true);

    if (enabled) {
      const currentLayout = LAYOUTS.find(
        layout => layout.name === layoutSelected
      );
      if (currentLayout?.maxGroups) {
        const initialGroups = Array.from(
          {length: currentLayout.maxGroups},
          (_, index) => (index === 0 ? categorySelected.map(cat => cat.id) : [])
        );
        setCategoryGroups(initialGroups);
      }
    } else {
      setCategoryGroups([]);
    }
  };

  const handleDragEnd = (result: any) => {
    const {destination, source} = result;

    if (!destination) return;

    const sourceGroupIndex = parseInt(source.droppableId.replace('group-', ''));
    const destGroupIndex = parseInt(
      destination.droppableId.replace('group-', '')
    );

    const newGroups = [...categoryGroups];

    if (sourceGroupIndex === destGroupIndex) {
      // Moving within the same group
      newGroups[sourceGroupIndex] = arrayMove(
        newGroups[sourceGroupIndex],
        source.index,
        destination.index
      );
    } else {
      // Moving between different groups
      const {
        source: updatedSource,
        destination: updatedDestination,
      } = moveBetweenArrays(
        newGroups[sourceGroupIndex],
        newGroups[destGroupIndex],
        source.index,
        destination.index
      );

      newGroups[sourceGroupIndex] = updatedSource;
      newGroups[destGroupIndex] = updatedDestination;
    }

    setCategoryGroups(newGroups);
    setHasUnsavedChanges(true);
  };

  const handleCategoryChange = (event: any, value: Category[]) => {
    setCategorySelected(value);
    setHasUnsavedChanges(true);

    if (!groupingEnabled || categoryGroups.length === 0) return;

    const categoriesIds = value.map(cat => cat.id);

    const selectedIdsSet = new Set(categoriesIds);
    const currentGroupIds = new Set();

    const cleanedGroup = categoryGroups.map(group => {
      return group.filter(id => {
        if (selectedIdsSet.has(id)) {
          currentGroupIds.add(id);
          return true;
        } else {
          return false;
        }
      });
    });

    const newCategoriesIds = [];

    selectedIdsSet.forEach(id => {
      if (!currentGroupIds.has(id)) {
        newCategoriesIds.push(id);
      }
    });

    if (newCategoriesIds.length === 0) {
      setCategoryGroups(cleanedGroup);
      return;
    }

    const newGroup = [...cleanedGroup];

    newGroup[0] = [...newGroup[0], ...newCategoriesIds];

    setCategoryGroups(newGroup);
  };

  const currentLayout = LAYOUTS.find(layout => layout.name === layoutSelected);
  const showGrouping = limitedCategories && currentLayout?.maxGroups;
  const emptyContent = !categories?.length;

  return (
    <SwitchableSection
      id={HOME_PAGE_SECTION_NAMES.PRODUCT_CATEGORY}
      title="Menú de categorías"
      subtitle="Muestra las categorías de tu tienda en la página de inicio."
      loading={loading}
      chipLabel={
        !hasCategoryLayoutFeature ? <PremiumFeatureBadge /> : undefined
      }
      classes={{
        root:
          emptyContent || !sectionEnabled ? classes.disabledRoot : undefined,
      }}
    >
      <section className={classes.container}>
        <Typography className={classes.subtitle}>
          1. Selecciona una estructura
        </Typography>
        <div className={classes.items}>
          {LAYOUTS.filter(layout => {
            return layout.businessModel.includes(businessModel);
          }).map(layout => (
            <ButtonBase
              key={layout.name}
              className={classes.itemButton}
              onClick={() => handleSelectLayout(layout.name)}
              // disabled={!hasCategoryLayoutFeature}
            >
              <div className={classes.item} data-hide-thumbnail>
                <span className={classes.itemLabel}>{layout.label}</span>
                <div
                  className={classes.checkbox}
                  data-hide-thumbnail
                  data-checked={layoutSelected === layout.name}
                ></div>
                <img
                  className={classes.layoutThumbnail}
                  src={layout.image}
                  alt={`dimension ${layout.name}`}
                />
              </div>
            </ButtonBase>
          ))}
        </div>
        {limitedCategories && (
          <>
            <Typography className={classes.subtitle}>
              2. Selecciona las categorías que aparecerán {`(max. 6)`}
            </Typography>
            <Autocomplete
              multiple
              id="tags-filled"
              value={categorySelected}
              options={categories}
              onChange={handleCategoryChange}
              disableClearable
              getOptionDisabled={option => {
                if (categorySelected.length >= CATEGORY_SELECTED_LIMIT) {
                  return true;
                }
                return !option.enabled;
              }}
              getOptionLabel={option => option.name}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    {...getTagProps({index})}
                  />
                ))
              }
              renderInput={params => (
                <CustomTextField {...params} variant="filled" multiline />
              )}
            />
            {showGrouping && (
              <>
                <FormControlLabel
                  control={
                    <IosSwitch
                      checked={groupingEnabled}
                      onChange={event =>
                        handleGroupingToggle(event.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Agrupar categorías por columnas"
                />
                {groupingEnabled && (
                  <Box className={classes.groupingSection}>
                    <Typography className={classes.subtitle}>
                      3. Organiza las columnas
                    </Typography>
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <div className={classes.groupContainer}>
                        {categoryGroups.map((group, groupIndex) => (
                          <Droppable
                            key={`group-${groupIndex}`}
                            droppableId={`group-${groupIndex}`}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={`${classes.groupBox} ${
                                  snapshot.isDraggingOver
                                    ? classes.draggingOver
                                    : ''
                                }`}
                              >
                                <Typography className={classes.groupTitle}>
                                  Grupo {groupIndex + 1}
                                </Typography>
                                {group.map((categoryId, index) => {
                                  const category = categories?.find(
                                    cat => cat.id === categoryId
                                  );
                                  if (!category) return null;
                                  return (
                                    <Draggable
                                      key={categoryId}
                                      draggableId={categoryId}
                                      index={index}
                                    >
                                      {provided => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                        >
                                          <div
                                            className={classes.categoryDragItem}
                                          >
                                            <DragIndicator
                                              className={classes.dragIcon}
                                            />
                                            <img
                                              className={classes.categoryImage}
                                              src={getImageUrl(
                                                category.portraitImageId
                                              )}
                                              alt={category.name}
                                            />
                                            <span>{category.name}</span>
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        ))}
                      </div>
                    </DragDropContext>
                  </Box>
                )}
              </>
            )}
          </>
        )}
        <div className={classes.saveButtonContainer}>
          <CustomButton
            variant="contained"
            color="primary"
            onClick={handleSaveCategoryMenu}
            disabled={!hasUnsavedChanges || loading}
          >
            {loading ? 'Guardando...' : 'Guardar cambios'}
          </CustomButton>
        </div>
      </section>
    </SwitchableSection>
  );
}
